import React, { useEffect, useMemo, useRef } from "react";
import hasContainer from "helpers/has-container";

/**
 * Document-level selectors (:root/html/body) do not match inside @scope,
 * so CSS variables and base rules defined on them never reach the content.
 * Map them to :scope so they apply to the isolated wrapper.
 */
const rewriteDocumentSelectorsForScope = (cssText) => {
    if (!cssText) {
        return "";
    }

    return cssText
        .replace(/:root\b/g, ":scope")
        .replace(/(^|[\s,+>~|(])(html|body)(?=[\s,+>~|{.:#[*)]|$)/gi, "$1:scope")
        .replace(/:scope(\s+:scope)+/g, ":scope")
        .replace(/:scope\s*,\s*:scope/g, ":scope");
};

const wrapCssWithScope = (cssText) => {
    if (!cssText || !cssText.trim()) {
        return cssText;
    }

    return ` @scope { ${rewriteDocumentSelectorsForScope(cssText)} } `;
};

/**
 * Unlayered inbound reset: stop site styles from matching inside the wrapper.
 * `all: initial` cuts inheritance from body/ancestors.
 * `all: revert` on descendants restores user-agent defaults so pasted CSS can restyle them.
 * Inherited properties are then set back to `inherit` so embed `body` / `:root` rules
 * (rewritten to `:scope`) still flow into children — `all: revert` would otherwise
 * pin line-height/font/color to UA values and ignore the wrapper.
 * `:where()` keeps this at zero specificity so embed selectors (h1, p, .class) win
 * without needing !important. Unlayered still beats Tailwind @layer base.
 * SVG is excluded: `all` overrides presentation attributes (width, height, fill)
 * and collapses inline icons.
 */
const INBOUND_RESET_STYLE = `<style>@scope {
  :where(:scope) {
    all: initial;
    display: block;
    box-sizing: border-box;
  }
  :where(:scope) :where(*:not(svg, svg *)),
  :where(:scope) :where(*:not(svg, svg *)::before),
  :where(:scope) :where(*:not(svg, svg *)::after) {
    all: revert;
    box-sizing: border-box;
    font: inherit;
    color: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    word-spacing: inherit;
    text-align: inherit;
    text-indent: inherit;
    text-transform: inherit;
    white-space: inherit;
    direction: inherit;
    visibility: inherit;
    cursor: inherit;
  }
}</style>`;

const EXECUTABLE_SCRIPT_TYPES = new Set([
    "",
    "text/javascript",
    "application/javascript",
    "text/ecmascript",
    "application/ecmascript",
    "module",
    "text/jscript",
]);

const parseScriptAttributes = (attrString) => {
    const attrs = {};
    if (!attrString) {
        return attrs;
    }

    const attrRe = /([^\s=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+)))?/g;
    let match = attrRe.exec(attrString);

    while (match) {
        attrs[match[1].toLowerCase()] = match[2] ?? match[3] ?? match[4] ?? "";
        match = attrRe.exec(attrString);
    }

    return attrs;
};

const isExecutableScriptType = (type) =>
    EXECUTABLE_SCRIPT_TYPES.has((type || "").trim().toLowerCase());

const isModuleScript = (type) => (type || "").trim().toLowerCase() === "module";

const escapeSelectorId = (id) => {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(id);
    }
    return String(id).replace(/([^\w-])/g, "\\$1");
};

/**
 * Pasted full-page scripts use document.getElementById / querySelector.
 * Those hit the real page first (Gatsby header, #main) and throw when a
 * referenced node is missing — aborting the rest of the IIFE (tabs, quiz).
 * Scope lookups to the isolated wrapper and return a no-op node instead of null.
 */
const createScopedDocument = (container) => {
    const stub = document.createElement("div");
    stub.addEventListener = () => {};
    stub.removeEventListener = () => {};
    stub.append = () => {};
    stub.appendChild = () => stub;
    stub.remove = () => {};
    stub.setAttribute = () => {};
    stub.getAttribute = () => null;
    stub.querySelector = () => null;
    stub.querySelectorAll = () => [];

    return new Proxy(document, {
        get(target, prop, receiver) {
            if (prop === "getElementById") {
                return (id) =>
                    container.querySelector(`#${escapeSelectorId(id)}`) || stub;
            }
            if (prop === "querySelector") {
                return (selector) => container.querySelector(selector);
            }
            if (prop === "querySelectorAll") {
                return (selector) => container.querySelectorAll(selector);
            }
            if (prop === "getElementsByClassName") {
                return (name) => container.getElementsByClassName(name);
            }
            if (prop === "getElementsByTagName") {
                return (tagName) => {
                    const tag = String(tagName).toLowerCase();
                    if (tag === "html" || tag === "body") {
                        return [container];
                    }
                    return container.getElementsByTagName(tagName);
                };
            }
            if (prop === "body" || prop === "documentElement" || prop === "scrollingElement") {
                return container;
            }

            const value = Reflect.get(target, prop, receiver);
            return typeof value === "function" ? value.bind(target) : value;
        },
    });
};

const wrapInlineScriptForScope = (code, scopeKey) => `(function(document){
try {
${code}
} catch (error) {
console.error("[isolated-html] Embedded script failed:", error);
}
})(window[${JSON.stringify(scopeKey)}]);`;

/**
 * Pasted tab/widget scripts often wait for DOMContentLoaded. In Gatsby the
 * document is already loaded, so those listeners would never run.
 * Fire them immediately while the inline script executes, then restore.
 */
const patchReadyEventListeners = () => {
    const documentReady = document.readyState !== "loading";
    const windowLoaded = document.readyState === "complete";

    if (!documentReady && !windowLoaded) {
        return () => {};
    }

    const documentOrig = document.addEventListener;
    const windowOrig = window.addEventListener;

    const maybeRunNow = (target, original, type, listener, options) => {
        const runNow =
            (documentReady && type === "DOMContentLoaded") ||
            (windowLoaded && type === "load");

        if (runNow && typeof listener === "function") {
            listener.call(target, new Event(type));
            return undefined;
        }

        return original.call(target, type, listener, options);
    };

    document.addEventListener = function patchedDocumentAdd(type, listener, options) {
        return maybeRunNow(document, documentOrig, type, listener, options);
    };
    window.addEventListener = function patchedWindowAdd(type, listener, options) {
        return maybeRunNow(window, windowOrig, type, listener, options);
    };

    return () => {
        document.addEventListener = documentOrig;
        window.addEventListener = windowOrig;
    };
};

const createScriptElement = (scriptDef) => {
    const attrs = parseScriptAttributes(scriptDef.attrs);
    if (!isExecutableScriptType(attrs.type)) {
        return null;
    }

    const scriptEl = document.createElement("script");

    Object.entries(attrs).forEach(([name, value]) => {
        if (name === "async" || name === "defer") {
            return;
        }
        scriptEl.setAttribute(name, value);
    });

    if (!attrs.src) {
        scriptEl.text = scriptDef.content || "";
    }

    return scriptEl;
};

const waitForScript = (scriptEl) => {
    if (!scriptEl.src && !isModuleScript(scriptEl.type)) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        scriptEl.onload = () => resolve();
        scriptEl.onerror = () => {
            console.error("[isolated-html] Failed to load script", scriptEl.src);
            resolve();
        };
    });
};

/**
 * innerHTML / dangerouslySetInnerHTML never execute <script> tags.
 * Recreate them as real script elements after mount, in source order.
 */
const injectScripts = async (container, scripts, injected, isCancelled) => {
    const scopeKey = `__isolatedHtmlDoc_${Math.random().toString(36).slice(2)}`;
    window[scopeKey] = createScopedDocument(container);

    if (process.env.NODE_ENV === "development") {
        console.info("[isolated-html] injecting scripts", scripts.map((scriptDef) => {
            const attrs = parseScriptAttributes(scriptDef.attrs);
            return {
                type: attrs.type || "text/javascript",
                src: attrs.src || null,
                inlineBytes: attrs.src ? 0 : (scriptDef.content || "").length,
            };
        }));
    }

    try {
        for (const scriptDef of scripts) {
            if (isCancelled()) {
                break;
            }

            const scriptEl = createScriptElement(scriptDef);
            if (!scriptEl) {
                continue;
            }

            const inlineClassic = !scriptEl.src && !isModuleScript(scriptEl.type);
            if (inlineClassic) {
                scriptEl.text = wrapInlineScriptForScope(scriptDef.content || "", scopeKey);
            }

            injected.push(scriptEl);

            const loaded = waitForScript(scriptEl);

            if (inlineClassic) {
                const restore = patchReadyEventListeners();
                try {
                    container.appendChild(scriptEl);
                } finally {
                    restore();
                }
            } else {
                container.appendChild(scriptEl);
            }

            await loaded;
        }
    } finally {
        delete window[scopeKey];
    }
};

/**
 * String-based processing so the same path works in Gatsby SSR and the browser.
 * Style tags are hoisted onto the wrapper so @scope (no prelude) uses that
 * element as the scope root, not a nested <head>.
 * Script tags are extracted (including those in <head>) so they can be
 * re-injected as real scripts after mount.
 */
const processIsolatedHtml = (html) => {
    if (!html || typeof html !== "string") {
        return { html: "", scripts: [] };
    }

    const styleTags = [];
    let remainingHtml = html.replace(
        /<style\b([^>]*)>([\s\S]*?)<\/style>/gi,
        (_match, attrs, css) => {
            styleTags.push(`<style${attrs}>${wrapCssWithScope(css)}</style>`);
            return "";
        }
    );

    const scripts = [];
    remainingHtml = remainingHtml.replace(
        /<script\b([^>]*)(?:\/>|>([\s\S]*?)<\/script>)/gi,
        (_match, attrs, content = "") => {
            scripts.push({ attrs: (attrs || "").trim(), content });
            return "";
        }
    );

    const bodyMatch = /<body\b[^>]*>([\s\S]*?)<\/body>/i.exec(remainingHtml);
    let bodyHtml = bodyMatch
        ? bodyMatch[1]
        : remainingHtml.replace(/<head\b[^>]*>[\s\S]*?<\/head>/i, "");

    bodyHtml = bodyHtml
        .replace(/<script\b[\s\S]*?<\/script>/gi, "")
        .replace(/<link\b[^>]*>/gi, "");

    return {
        html: `${INBOUND_RESET_STYLE}${styleTags.join("")}${bodyHtml}`,
        scripts,
    };
};

const IsolatedHtml = ({ data }) => {
    const containerRef = useRef(null);
    const hasModuleContainer = hasContainer(data?.moduleSettings?.hasContainer) === "container";
    const { html, scripts } = useMemo(
        () => processIsolatedHtml(data?.html),
        [data?.html]
    );

    useEffect(() => {
        const container = containerRef.current;
        if (!container || scripts.length === 0) {
            return undefined;
        }

        let cancelled = false;
        const injected = [];

        injectScripts(container, scripts, injected, () => cancelled).then(() => {
            if (cancelled) {
                injected.forEach((el) => el.remove());
            }
        });

        return () => {
            cancelled = true;
            injected.forEach((el) => {
                el.onload = null;
                el.onerror = null;
                el.remove();
            });
        };
    }, [scripts]);

    return (
        <div className={hasModuleContainer ? "container" : "p-0"}>
            <div
                ref={containerRef}
                className="isolated-html"
                style={hasModuleContainer ? { maxWidth: "1200px", display: "block" } : { display: "block" }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
};

export default IsolatedHtml;
